import { useScrollAnimation, useStaggerAnimation } from '../../../hooks/useScrollAnimation';

const diferenciais = [
  {
    icon: 'ri-focus-3-line',
    title: 'Decisão Técnica',
    description: 'Cada escolha é baseada em análise de cobertura, cláusulas e custo-benefício real — não em feeling ou preço.',
  },
  {
    icon: 'ri-eye-2-line',
    title: 'Transparência Total',
    description: 'Você entende exatamente o que está contratando, quanto custa de comissão e o que está incluso.',
  },
  {
    icon: 'ri-user-settings-line',
    title: 'Controle do Segurado',
    description: 'O segurado passa a conduzir a contratação com critério próprio, sem depender exclusivamente do corretor.',
  },
  {
    icon: 'ri-flow-chart',
    title: 'Processo Estruturado',
    description: 'Um passo a passo validado que elimina erros comuns e garante contratações mais seguras e eficientes.',
  },
  {
    icon: 'ri-award-line',
    title: 'Método Validado',
    description: 'Mais de 4.800 segurados aplicaram o método com resultado comprovado em economia e proteção real.',
  },
  {
    icon: 'ri-scales-3-line',
    title: 'Independência na Decisão',
    description: 'Você compara propostas com critério técnico e decide sem pressão comercial ou conflito de interesse.',
  },
];

export default function DiferenciaisMetodo() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: gridRef, visibleItems } = useStaggerAnimation(diferenciais.length);
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation();

  return (
    <section
      id="diferenciais-metodo"
      className="py-16 sm:py-20 lg:py-28 bg-gradient-to-br from-teal-50/50 to-white"
      aria-labelledby="diferenciais-metodo-heading"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Cabeçalho */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-12 sm:mb-16 transition-all duration-700 ease-out ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 bg-teal-100 px-4 py-2 rounded-full mb-4 sm:mb-6">
            <i className="ri-award-line text-teal-600 text-base" aria-hidden="true"></i>
            <span className="text-teal-700 font-bold text-xs sm:text-sm uppercase tracking-wide">Método de Contratação Ativa</span>
          </div>
          <h2
            id="diferenciais-metodo-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-petrol-900 mb-4 sm:mb-6 px-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            O que torna esse método{' '}
            <span className="text-teal-600">diferente de tudo</span>
          </h2>
          <p className="text-base sm:text-lg text-petrol-700 max-w-3xl mx-auto leading-relaxed px-4">
            Enquanto o mercado tradicional decide por você, o{' '}
            <strong className="text-petrol-900">Método de Contratação Ativa</strong> devolve o controle para quem
            realmente importa: o segurado.
          </p>
        </div>

        {/* Grid de cards */}
        <div
          ref={gridRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16"
        >
          {diferenciais.map((item, index) => (
            <div
              key={index}
              className={`bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 border border-teal-100 hover:border-teal-400 group cursor-default transition-all duration-700 ease-out ${visibleItems[index] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            >
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                <i className={`${item.icon} text-2xl sm:text-3xl text-white`} aria-hidden="true"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-petrol-900 mb-3 sm:mb-4 group-hover:text-teal-600 transition-colors leading-tight">
                {item.title}
              </h3>
              <p className="text-sm sm:text-base text-petrol-700 leading-relaxed">
                {item.description}
              </p>
            </div>
          ))}
        </div>

        {/* CTA inferior */}
        <div
          ref={ctaRef as React.RefObject<HTMLDivElement>}
          className={`bg-gradient-to-r from-petrol-900 to-teal-900 rounded-xl sm:rounded-2xl p-6 sm:p-10 text-center transition-all duration-700 ease-out ${ctaVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'}`}
        >
          <h3
            className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-3 sm:mb-4 px-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Pronto para contratar seguro com critério técnico?
          </h3>
          <p className="text-sm sm:text-base text-gray-200 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
            O método completo por apenas{' '}
            <strong className="text-teal-300">R$67</strong>. Acesso imediato, sem risco.
          </p>
          <div className="text-center">
            <a
              href="#analise-gratuita"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-action-orange-500 text-white text-lg font-semibold rounded-lg hover:bg-action-orange-600 transition-all duration-300 whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-book-open-line text-white text-lg" aria-hidden="true"></i>
              </div>
              Quero acessar o método agora
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line text-white" aria-hidden="true"></i>
              </div>
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
